import { setUser as sentrySetUser } from '@sentry/react';
import { Container } from '@mantine/core';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchLanguages } from '../../../libraries/language';
import { fetchRegions } from '../../../libraries/region';
import { IUser, PermissionOption, fetchUser, fetchPermissions } from '../../../libraries/user';
import { AppState } from '../../client-redux/reducers';
import { IEntity } from '../../../libraries/entity';
import Header from '../../components/util/header';
import { Language } from '../../languages/languageHandler';
import { preparePermissions } from '../users/permissions';
import { selectedLang } from '../../client-redux/languages';
import UserForm, { UserFormMode } from '../users/user-form';
import { AppDispatch } from '../../client-redux/store';
import { isZarphSuccessResponse, useStatusOperDialog } from '../../context/status-oper-dialog';
import './styles.scss';

const Profile = () => {
  const loginInfo = useSelector<AppState, IUser>(state => state.login.user);
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const entities = useSelector<AppState, IEntity[]>(state => state.entities.entities);
  const dispatch = useDispatch<AppDispatch>();

  const [user, setUser] = useState<IUser | null>(null);
  const [permissions, setPermissions] = useState<PermissionOption[]>([]);

  const { handleApiError } = useStatusOperDialog();

  useEffect(() => {
    loadProfileData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginInfo]);

  const loadProfileData = async () => {
    try {
      setUser(null);
      const userResponse = await fetchUser(loginInfo.entid, loginInfo.uid ?? 0);
      if (!isZarphSuccessResponse(userResponse) || !userResponse.data.users?.length) {
        handleApiError(userResponse, lang.EDIT_USER_ALERT_UPDATE_ERROR);
        return;
      }
      const fetchedUser = userResponse.data.users[0];
      setUser(fetchedUser);
      sentrySetUser(fetchedUser);

      const [permResponse] = await Promise.all([fetchPermissions(), fetchRegions(), fetchLanguages()]);
      if (!isZarphSuccessResponse(permResponse)) {
        handleApiError(permResponse, lang.EDIT_USER_ALERT_UPDATE_ERROR);
        return;
      }
      preparePermissions(permResponse.data.list, fetchedUser);
      setPermissions(permResponse.data.list);
    } catch (err) {
      handleApiError(err, lang.EDIT_USER_ALERT_UPDATE_ERROR);
    }
  };

  return (
    <Container fluid className="profile">
      <Header pageTitle={lang.USER_MY_PROFILE} />

      {user && (
        <UserForm
          mode={UserFormMode.UPDATE_PROFILE}
          isNew={false}
          user={user}
          lang={lang}
          permissionOptions={permissions}
          togglePermissions={() => {}}
          onSuccess={msg => {
            handleApiError({ statusOper: { code: 0, message: msg } }, lang.EDIT_USER_ALERT_UPDATE_SUCCESS);
          }}
          onError={err => {
            handleApiError(err, lang.EDIT_USER_ALERT_UPDATE_ERROR);
          }}
          entities={entities}
          changeLanguage={(langId: number) => dispatch(selectedLang(langId))}
        />
      )}
    </Container>
  );
};

export default Profile;
